import React, { useState, useEffect } from 'react';
import {
  X,
  Folder,
  Tag,
  Trash2,
  AlertTriangle,
  Check,
  Plus,
} from 'lucide-react';

// Move Card to Category Modal
export function MoveCardCategoryModal({
  open,
  card,
  categories = [],
  onClose,
  onSave,
}) {
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    if (card) {
      setSelectedCategory(card.category || categories[0]?.name || 'Finance');
    }
  }, [card, categories]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (open) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  if (!open || !card) return null;

  const handleSave = (e) => {
    e.preventDefault();
    onSave(card, selectedCategory);
    onClose();
  };

  const allCategories = [
    { id: 'cat-finance', name: 'Finance', icon: '💳' },
    { id: 'cat-personal', name: 'Personal', icon: '👤' },
    { id: 'cat-business', name: 'Business', icon: '💼' },
    { id: 'cat-travel', name: 'Travel', icon: '✈️' },
    { id: 'cat-shopping', name: 'Shopping', icon: '🛍️' },
    ...categories.filter(
      (c) => !['Finance', 'Personal', 'Business', 'Travel', 'Shopping'].includes(c.name)
    ),
  ];

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal-container identity-action-modal-container"
        role="dialog"
        aria-modal="true"
        aria-label="Move Card to Category"
      >
        <div className="modal-header">
          <div className="identity-activity-title-group">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366f1' }}
            >
              <Folder size={18} />
            </div>
            <div>
              <h3 className="modal-title">Move to Category</h3>
              <p className="modal-subtitle">
                Choose a destination category for "{card.cardName || card.title || 'Untitled Card'}".
              </p>
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSave} className="identity-editor-form">
          <div className="modal-body">
            <div className="category-select-grid">
              {allCategories.map((cat) => (
                <label
                  key={cat.id || cat.name}
                  className={`category-select-option ${
                    selectedCategory === cat.name ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.name}
                    checked={selectedCategory === cat.name}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={{ display: 'none' }}
                  />
                  <span className="category-option-icon">{cat.icon || '📁'}</span>
                  <span className="category-option-name">{cat.name}</span>
                  {selectedCategory === cat.name && (
                    <Check size={16} className="category-option-check" />
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="modal-footer identity-editor-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Move Card
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Manage Card Tags Modal
export function ManageCardTagsModal({
  open,
  card,
  onClose,
  onSave,
}) {
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (card) {
      setTags(Array.isArray(card.tags) ? [...card.tags] : []);
      setTagInput('');
    }
  }, [card]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (open) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  if (!open || !card) return null;

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = tagInput.trim().toLowerCase().replace(/^#/, '');
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput('');
    }
  };

  const handleAddTagClick = () => {
    const val = tagInput.trim().toLowerCase().replace(/^#/, '');
    if (val && !tags.includes(val)) {
      setTags([...tags, val]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSave(card, tags);
    onClose();
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal-container identity-action-modal-container"
        role="dialog"
        aria-modal="true"
        aria-label="Manage Card Tags"
      >
        <div className="modal-header">
          <div className="identity-activity-title-group">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}
            >
              <Tag size={18} />
            </div>
            <div>
              <h3 className="modal-title">Manage Tags</h3>
              <p className="modal-subtitle">
                Label and organize "{card.cardName || card.title || 'Untitled Card'}"
              </p>
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSave} className="identity-editor-form">
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Add New Tag</label>
              <div className="tag-input-row" style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. cashback, personal, business, recurring"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddTagClick}
                >
                  <Plus size={16} />
                  <span>Add</span>
                </button>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '16px' }}>
              <label className="form-label">Current Tags ({tags.length})</label>
              <div
                className="tags-chip-container"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  minHeight: '42px',
                  padding: '10px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                }}
              >
                {tags.length === 0 ? (
                  <span className="text-secondary text-sm">No tags added yet.</span>
                ) : (
                  tags.map((tag) => (
                    <span key={tag} className="tag-chip">
                      #{tag}
                      <button
                        type="button"
                        className="tag-remove-btn"
                        onClick={() => handleRemoveTag(tag)}
                      >
                        &times;
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer identity-editor-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Tags
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete Card Modal (Soft Delete or Permanent)
export function DeleteCardModal({
  open,
  card,
  isPermanent = false,
  onClose,
  onConfirm,
}) {
  // Lock body scroll on mobile
  useEffect(() => {
    if (open) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  if (!open || !card) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal-container identity-action-modal-container"
        role="dialog"
        aria-modal="true"
        aria-label="Delete Card"
      >
        <div className="modal-header">
          <div className="identity-activity-title-group">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}
            >
              <AlertTriangle size={18} />
            </div>
            <div>
              <h3 className="modal-title">
                {isPermanent ? 'Permanently Delete Payment Card' : 'Move Card to Trash'}
              </h3>
              <p className="modal-subtitle">
                {isPermanent
                  ? 'This action is irreversible. All encrypted payment details and scans will be destroyed.'
                  : 'You can restore this card from the Trash anytime.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <p style={{ margin: '10px 0', fontSize: '14px', lineHeight: 1.5 }}>
            Are you sure you want to {isPermanent ? 'permanently erase' : 'move'}{' '}
            <strong>"{card.cardName || card.title || 'Untitled Card'}"</strong>
            {isPermanent
              ? ' from your vault? The cryptographic key and attached card scans will be purged permanently.'
              : ' to the trash?'}
          </p>
        </div>

        <div className="modal-footer identity-editor-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              onConfirm(card);
              onClose();
            }}
          >
            <Trash2 size={16} />
            <span>{isPermanent ? 'Erase Forever' : 'Move to Trash'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
